/**
 * Section: "Helyszín-emlékeztető" (relevance). A locations[] list (lat/lng/relevantText
 * per row) up to a client-side display-hint cap of 10, a single shared maxDistance
 * (template radius), and relevantDate/expirationDate.
 *
 * Seed-populated location rows and the two dates are [LIVE] — read-only with the
 * badge (refreshed at mint from the real departure airport / flight window).
 * Operator-added rows are a DESIGN-TIME PREVIEW only: buildLivePatch replaces
 * relevance.locations wholesale at mint with the live airport surroundings, so a
 * note warns the operator their test pins do not ship.
 *
 * The server validatePassDesign is the authoritative >10 gate; this editor only
 * shows the n / 10 hint, disables "+ Hely" at 10, and renders the server-returned
 * relevance.locations error inline.
 */
import type {
  DeepPartial,
  PassDesign,
  PassDesignError,
  PassDesignLocation,
} from '@insurwiz/wallet-core';

import { HelpHint, HelpText } from '../designer/HelpHint';
import { LiveBadge } from '../designer/LiveBadge';

import { liveRelevanceKeys } from './seedLiveKeys';

const MAX_LOCATIONS = 10;

/** Neutral (no-flight) explanation for the [LIVE] date locks on the generic canvas. */
const GENERIC_LIVE_EXPLANATION =
  'Az értéket a rendszer adja a pass létrehozásakor, a generáláskor megadott adatokból. ' +
  'Itt csak a címkéjét látod; a mezőt mintáláskor a rendszer tölti ki.';

export interface RelevanceEditorProps {
  design: PassDesign;
  seed: PassDesign;
  errors: PassDesignError[];
  onChange: (patch: DeepPartial<PassDesign>) => void;
  /**
   * false on the generic (no-flight) canvas: the copy loses every flight/
   * airport reference, and the seed's airport pin does NOT lock the location
   * rows — general location reminders are operator-authored there, so every
   * row stays editable/removable. Defaults to true (the flight-bound travel
   * canvas keeps the pre-existing behaviour verbatim).
   */
  flightBound?: boolean;
}

export function RelevanceEditor({
  design,
  seed,
  errors,
  onChange,
  flightBound = true,
}: RelevanceEditorProps): JSX.Element {
  const liveKeys = new Set(liveRelevanceKeys(seed));
  const relevance = design.relevance ?? {};
  const locations = relevance.locations ?? [];
  const locationsLive = flightBound && liveKeys.has('locations');
  // Seed-populated rows (the first `seedLocationCount`) are [LIVE] read-only — the mint
  // replaces them with the live airport geofence. Operator-added preview rows beyond that
  // count stay editable/removable, up to the client-side display-hint cap.
  const seedLocationCount = seed.relevance?.locations?.length ?? 0;
  const isSeedRow = (index: number): boolean => locationsLive && index < seedLocationCount;
  const locationsErrors = errors.filter((e) => e.path === 'relevance.locations');

  function writeLocations(next: PassDesignLocation[]): void {
    onChange({ relevance: { locations: next } });
  }
  function addLocation(): void {
    writeLocations([...locations, { latitude: 0, longitude: 0 }]);
  }
  function removeLocation(index: number): void {
    writeLocations(locations.filter((_, i) => i !== index));
  }
  function patchLocation(index: number, patch: Partial<PassDesignLocation>): void {
    writeLocations(locations.map((loc, i) => (i === index ? { ...loc, ...patch } : loc)));
  }

  return (
    <section className="card p-4 space-y-3" aria-label="Helyszín-emlékeztető">
      <h3 className="text-sm font-semibold text-slate-800">Helyszín-emlékeztető</h3>
      <HelpText>
        {flightBound
          ? 'A pass a reptér közelében / a járat idején felugrik a zárolt képernyőre. A helyeket mintáláskor a valós reptér adja.'
          : 'A pass a megadott helyek közelében megjelenik a zárolt képernyőn — pl. egy iroda vagy ügyfélpont környékén.'}
      </HelpText>

      {flightBound ? (
        <p className="rounded bg-amber-50 p-2 text-xs text-amber-800">
          A helyek csak előnézet — mintáláskor a valós reptér környéke váltja fel őket. A hatótáv
          megmarad.
        </p>
      ) : null}

      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
          Helyek{locationsLive ? <LiveBadge /> : null}
        </span>
        <span className="text-xs text-slate-500">
          {locations.length} / {MAX_LOCATIONS}
        </span>
      </div>

      {locationsErrors.map((e, i) => (
        <p key={`locerr-${String(i)}`} className="text-xs text-red-600">
          {e.message}
        </p>
      ))}

      {locations.map((loc, index) => {
        const rowLive = isSeedRow(index);
        return (
          // min-w-0 on every input: a grid item's default min-width is its
          // content's intrinsic min-width, so the inputs' native size would
          // force this row to ~580px and overflow a narrow (~380px) host
          // column — min-w-0 lets the fr tracks actually shrink.
          <div key={`loc-${String(index)}`} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2">
            <input
              aria-label={`Szélesség ${String(index)}`}
              type="number"
              className="input min-w-0"
              readOnly={rowLive}
              value={loc.latitude}
              onChange={
                rowLive
                  ? undefined
                  : (ev) => patchLocation(index, { latitude: Number(ev.target.value) })
              }
            />
            <input
              aria-label={`Hosszúság ${String(index)}`}
              type="number"
              className="input min-w-0"
              readOnly={rowLive}
              value={loc.longitude}
              onChange={
                rowLive
                  ? undefined
                  : (ev) => patchLocation(index, { longitude: Number(ev.target.value) })
              }
            />
            <input
              aria-label={`Emlékeztető szöveg ${String(index)}`}
              type="text"
              className="input min-w-0"
              readOnly={rowLive}
              value={loc.relevantText ?? ''}
              onChange={
                rowLive
                  ? undefined
                  : (ev) => patchLocation(index, { relevantText: ev.target.value })
              }
            />
            {rowLive ? null : (
              <button
                type="button"
                aria-label={`Helyszín törlése ${String(index)}`}
                className="text-xs text-red-600"
                onClick={() => removeLocation(index)}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className="btn-secondary text-xs"
        disabled={locations.length >= MAX_LOCATIONS}
        onClick={addLocation}
      >
        + Hely
      </button>

      {/* HelpHint/LiveBadge buttons stay OUTSIDE the <label>: a button inside the label
          would become its first labelable descendant and steal the association from the
          input (see SemanticsEditor / BarcodeEditor for the same htmlFor/id idiom). */}
      <div className="text-xs text-slate-600">
        <div className="flex items-center gap-1">
          <label htmlFor="relevance-maxDistance">Hatótáv (méter)</label>
          <HelpHint
            text="Ekkora körben aktiválódik a helyszín-emlékeztető, pl. 1000 m."
            label="Hatótáv súgó"
          />
        </div>
        <input
          id="relevance-maxDistance"
          aria-label="Hatótáv (méter)"
          type="number"
          className="input mt-1 w-full"
          value={relevance.maxDistance ?? ''}
          onChange={(e) => {
            const v = e.target.value.trim();
            onChange(
              v === ''
                ? { relevance: { maxDistance: null } }
                : { relevance: { maxDistance: Number(v) } },
            );
          }}
        />
      </div>

      <div className="text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <label htmlFor="relevance-relevantDate">Mikortól aktuális</label>
          {liveKeys.has('relevantDate') ? (
            <LiveBadge explanation={flightBound ? undefined : GENERIC_LIVE_EXPLANATION} />
          ) : null}
        </div>
        <input
          id="relevance-relevantDate"
          aria-label="Mikortól aktuális"
          type="text"
          className="input mt-1 w-full"
          readOnly={liveKeys.has('relevantDate')}
          value={relevance.relevantDate ?? ''}
          onChange={
            liveKeys.has('relevantDate')
              ? undefined
              : (e) => onChange({ relevance: { relevantDate: e.target.value } })
          }
        />
      </div>

      <div className="text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <label htmlFor="relevance-expirationDate">Meddig érvényes</label>
          {liveKeys.has('expirationDate') ? (
            <LiveBadge explanation={flightBound ? undefined : GENERIC_LIVE_EXPLANATION} />
          ) : null}
        </div>
        <input
          id="relevance-expirationDate"
          aria-label="Meddig érvényes"
          type="text"
          className="input mt-1 w-full"
          readOnly={liveKeys.has('expirationDate')}
          value={relevance.expirationDate ?? ''}
          onChange={
            liveKeys.has('expirationDate')
              ? undefined
              : (e) => onChange({ relevance: { expirationDate: e.target.value } })
          }
        />
      </div>
    </section>
  );
}
